/**
 * GamificationBar Component
 * Displays player level, rank title, XP progress bar, current streak, coins,
 * and quick-access buttons for Daily Planning and Analytics.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Coins, Trophy, Zap, BarChart2, Sun, Grid } from 'lucide-react';
import { getStreakMultiplier } from '../hooks/useGamification';

export function GamificationBar({
  levelInfo,
  rankInfo,
  gameState,
  totalCompletedToday,
  todayPlannedCount = 0,
  viewMode = 'matrix',
  onToggleViewMode,
  onOpenAnalytics,
}) {
  const { level, currentLevelXP, nextLevelXP, progressPercent } = levelInfo;
  const multiplier = getStreakMultiplier(gameState.currentStreak);

  return (
    <div className="game-bar-container">
      {/* Level & Rank Badge */}
      <div className="game-badge-group">
        <span className="game-level-tag">
          <Trophy size={14} className="level-trophy-icon" />
          <span>Lv.{level}</span>
        </span>
        <span className="game-rank-name">
          {rankInfo.badge} {rankInfo.title}
        </span>
      </div>

      {/* XP Progress Bar */}
      <div className="game-xp-wrapper">
        <div className="game-xp-labels">
          <span className="xp-text">
            <Zap size={12} className="xp-zap-icon" /> EXP
          </span>
          <span className="xp-fraction">
            {currentLevelXP} / {nextLevelXP} XP ({progressPercent}%)
          </span>
        </div>
        <div className="game-xp-track">
          <motion.div
            className="game-xp-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Stats Group & Action Buttons */}
      <div className="game-stats-group">
        <div className={`game-stat-chip streak-chip ${gameState.currentStreak > 0 ? 'active' : ''}`} title="Chuỗi ngày liên tục hoàn thành task">
          <Flame size={15} className="streak-flame-icon" />
          <span className="stat-value">{gameState.currentStreak} ngày</span>
          {multiplier > 1.0 && (
            <span className="streak-multiplier-tag">x{multiplier}</span>
          )}
        </div>

        <div className="game-stat-chip coins-chip" title="Gold Coins tích lũy">
          <Coins size={15} className="coins-icon" />
          <span className="stat-value">{gameState.coins}</span>
        </div>

        {/* View Mode Toggle: Matrix vs Today's Focus */}
        {onToggleViewMode && (
          <button
            className={`game-action-btn view-toggle-btn ${viewMode === 'daily' ? 'active-daily' : ''}`}
            onClick={onToggleViewMode}
            title={viewMode === 'daily' ? 'Chuyển về xem Ma trận' : 'Chuyển sang Kế hoạch hôm nay'}
          >
            {viewMode === 'daily' ? (
              <>
                <Grid size={14} />
                <span>Ma Trận</span>
              </>
            ) : (
              <>
                <Sun size={14} className="text-amber-500" />
                <span>Hôm nay {todayPlannedCount > 0 ? `(${todayPlannedCount})` : ''}</span>
              </>
            )}
          </button>
        )}

        {/* Analytics Modal Button */}
        {onOpenAnalytics && (
          <button
            className="game-action-btn analytics-btn"
            onClick={onOpenAnalytics}
            title="Xem bảng thống kê tiến độ"
          >
            <BarChart2 size={15} />
            <span className="analytics-btn-text">Thống kê</span>
          </button>
        )}
      </div>
    </div>
  );
}
